import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  styled,
  Tooltip,
} from '@mui/material';
import { DialogDefault, InfoTooltip, ThemeButton, ThemeTypography } from 'DesignSystem';
import { Field, Form, Formik, FormikProps } from 'formik';
import { Switch } from 'formik-mui';
import React, { ChangeEvent, FC, useRef } from 'react';
import {
  IOriginTableSettings,
  TColumnKeys,
} from 'FeatureModules/DataImport/types/dataImport.types';
import {
  TPlotGeometryDatasetColumn,
  EDatasetColumn,
} from 'FeatureModules/DataImport/types/dataset.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import {
  CoordinateDatasetColumnName,
  DatasetColumnSampleData,
} from 'FeatureModules/DataImport/utils/dataset.utils';
import {
  LandOwnershipDescriptions,
  LandOwnershipLabels,
} from 'FeatureModules/Site/utils/site.utils';
import FlexBox from 'Components/FlexBox';
import PageSubTitle from 'Components/PageSubTitle';
import { originTableSettingsSchema } from 'FeatureModules/DataImport/schemas/dataImport.schemas';
import SettingColumItem from '../SettingColumItem';

interface IDatasetTableSettingsDialogsProps extends IDefaultDialogProps {
  /**
   * Hides the top two checkboxes for the import settings
   */
  hideImportSettings?: boolean;
  settings: IOriginTableSettings;
  onTableSettingsChange: (settings: IOriginTableSettings) => void;
}

const BackgroundContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.custom.themeColors.grayScale[5],
  padding: theme.spacing(0, 2),
}));

const StyledTooltip = styled(InfoTooltip)(() => ({
  margin: 0,
}));

const Header = styled(ThemeTypography)(() => ({
  fontFamily: 'Lato',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '16px',
  textTransform: 'uppercase',
}));

const DatasetTableSettingsDialogs: FC<IDatasetTableSettingsDialogsProps> = ({
  open,
  settings,
  hideImportSettings,
  onClose,
  onTableSettingsChange,
}) => {
  const formRef = useRef<FormikProps<IOriginTableSettings>>(null);

  const initialValues = {
    ...originTableSettingsSchema.default(),
    ...settings,
  };

  const coordinateFormatOptions = (
    Object.entries(CoordinateDatasetColumnName) as [TPlotGeometryDatasetColumn, string][]
  ).map(([key, label]) => ({
    key,
    label,
    sampleData: DatasetColumnSampleData[key],
  }));

  const farmSizeFormatOptions = [
    {
      key: EDatasetColumn.FARM_SIZE_KM2,
      label: 'Square km',
      sampleData: DatasetColumnSampleData[EDatasetColumn.FARM_SIZE_KM2],
    },
    {
      key: EDatasetColumn.FARM_SIZE_HA,
      label: 'Hectares',
      sampleData: DatasetColumnSampleData[EDatasetColumn.FARM_SIZE_HA],
    },
  ];

  const plotSizeFormatOptions = [
    {
      key: EDatasetColumn.PLOT_SIZE_KM2,
      label: 'Square km',
      sampleData: DatasetColumnSampleData[EDatasetColumn.PLOT_SIZE_KM2],
    },
    {
      key: EDatasetColumn.PLOT_SIZE_HA,
      label: 'Hectares',
      sampleData: DatasetColumnSampleData[EDatasetColumn.PLOT_SIZE_HA],
    },
  ];

  const handleClose = () => onClose?.();

  const handleSubmitSettings = (values: IOriginTableSettings) => {
    onTableSettingsChange(values);
    onClose?.();
  };

  const handleToggleSelectAll = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const values = formRef.current?.values;
    if (!formRef.current || !values?.columns) {
      console.error('Columns are not defined');
      return;
    }
    (Object.keys(values.columns) as TColumnKeys[]).forEach(key => {
      // Skip geometry and plotID which are always required
      if (key === 'geometry' || key === 'plotId') {
        return;
      }
      // @ts-ignore
      values.columns[key] = { ...values.columns[key], enabled: checked };
    });
    formRef.current.setValues(values);
  };

  return (
    <DialogDefault
      open={!!open}
      title="Import settings"
      maxWidth="xl"
      fullWidth
      onClose={handleClose}
    >
      <>
        <Formik<IOriginTableSettings>
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={originTableSettingsSchema}
          onSubmit={handleSubmitSettings}
        >
          {({ isSubmitting, isValid, values }) => (
            <Form>
              <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} maxHeight="75vh">
                  {!hideImportSettings && (
                    <>
                      <BackgroundContainer width="fit-content">
                        <ThemeTypography variant="BODY_MEDIUM">
                          Import and update farm data for existing farm ids:
                        </ThemeTypography>
                        <Field
                          name="overwriteExisting"
                          fullWidth
                          component={Switch}
                          type="checkbox"
                        />
                        <StyledTooltip
                          variant="INFO"
                          text="Enable this to update the existing farm data with the new imported farm data for the Farm ids that already exist in the platform. The old farm data will be kept as history in the platform and any changes in the imported data will become the new current status for this farm."
                        />
                      </BackgroundContainer>

                      <BackgroundContainer width="fit-content">
                        <ThemeTypography variant="BODY_MEDIUM">
                          Add all imported farms to the same origin cluster:
                        </ThemeTypography>
                        <Tooltip
                          title={
                            values.columns.originClusterId.enabled
                              ? 'The column origin cluster ID is enabled, so clustering all farms to the same origin cluster is disabled. You can disable the column to enable this option.'
                              : undefined
                          }
                        >
                          <div>
                            <Field
                              name="clusterSites"
                              fullWidth
                              component={Switch}
                              type="checkbox"
                              // Disable if the origin cluster id column is enabled
                              disabled={values.columns.originClusterId.enabled}
                            />
                          </div>
                        </Tooltip>
                        <StyledTooltip
                          variant="INFO"
                          text="Enable this to cluster all farms of each imported dataset to an origin cluster unique per country and partner. Disable this to not cluster farms and store them individually per partner. An origin cluster is a group of sites at the origin that output the same component (e.g. a cluster of cocoa farms)."
                        />
                      </BackgroundContainer>
                    </>
                  )}

                  <BackgroundContainer width="100%">
                    <Box width="100%" py={2}>
                      <PageSubTitle title="Required data" />
                      <Divider />

                      <FlexBox flexDirection="column" gap={1} mt={2}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Header variant="BODY_MEDIUM">Data column</Header>
                          </Grid>

                          <Grid item xs={4}>
                            <Header variant="BODY_MEDIUM">Data format / unit</Header>
                          </Grid>

                          <Grid item xs={4}>
                            <Header variant="BODY_MEDIUM">Sample data</Header>
                          </Grid>
                        </Grid>

                        <Field
                          required
                          component={SettingColumItem}
                          name="columns.geometry"
                          label="Plot geometry"
                          infoTooltip={
                            <>
                              The format that is currently supported for plots in this table is
                              Well-known text (WKT). Read more about WKT format{' '}
                              <a
                                href="https://en.wikipedia.org/wiki/Well-known_text_representation_of_geometry"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                here
                              </a>
                            </>
                          }
                          // infoTooltip="The different kind of coordinates formats that are currently supported are: Well-known text (WKT), Signed DD, DD, DDM, DDS, DMS. The two coordinates need to be separated by a comma or space while the latitude needs to be in range of -90 to +90 and the longitude in range of -180 to +180."
                          formatOptions={coordinateFormatOptions}
                        />

                        <Field
                          required
                          component={SettingColumItem}
                          name="columns.plotId"
                          label="Plot ID"
                          infoTooltip="Unique ID for the plot within the farm. Makes it possible to update the plot data on future imports."
                          sampleData="eqs-dfa23HKS3"
                        />
                      </FlexBox>
                    </Box>
                  </BackgroundContainer>

                  <BackgroundContainer width="100%">
                    <Box width="100%" py={2}>
                      <PageSubTitle title="Extended data" />
                      <Divider />

                      <FlexBox flexDirection="column" gap={1} mt={2}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <FlexBox>
                              <Checkbox
                                checked={Object.values(values.columns).every(
                                  column => column.enabled
                                )}
                                onChange={handleToggleSelectAll}
                              />
                              <Header variant="BODY_MEDIUM">Data column</Header>
                            </FlexBox>
                          </Grid>

                          <Grid item xs={4}>
                            <FlexBox height="100%">
                              <Header variant="BODY_MEDIUM">Data format / unit</Header>
                            </FlexBox>
                          </Grid>

                          <Grid item xs={4}>
                            <FlexBox height="100%">
                              <Header variant="BODY_MEDIUM">Sample data</Header>
                            </FlexBox>
                          </Grid>
                        </Grid>

                        <Field
                          component={SettingColumItem}
                          name="columns.originClusterId"
                          label="Origin cluster ID"
                          infoTooltip="Unique ID if you want to cluster your farms. The id can reference an existing cluster in the platform, otherwise a new cluster will be created with that ID. All farms that have this cluster ID will be grouped together."
                          sampleData="12345678"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.farmId"
                          label="Farm ID"
                          infoTooltip="Unique ID for the farm. Makes it possible to update the farm data on future imports."
                          sampleData="3pEM-jal0HLY8"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.farmName"
                          label="Farm name"
                          infoTooltip="'Agricultural farm name, if not specified in the import the name will be set to 'Unnamed Farm'."
                          sampleData="Green Valley Farm"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.plotName"
                          label="Plot name"
                          infoTooltip="'Plot name, if not specified in the import the name will be set to 'Unnamed Plot'."
                          sampleData="North Field"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.plotSize"
                          label="Plot size"
                          infoTooltip="Size of the plot in the specified unit. This only needs to be provided if the plot geometry is a point. Otherwise the size will be calculated based on the provided geometry."
                          formatOptions={plotSizeFormatOptions}
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.farmSize"
                          label="Farm size"
                          infoTooltip="Size of the farm in the specified unit. This includes the area of all plots within the farm plus any additional area that is part of the farm but not mapped as plots."
                          formatOptions={farmSizeFormatOptions}
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.farmOwnerFullName"
                          label="Farm owner full name"
                          sampleData="John Doe"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.farmOwnerFirstName"
                          label="Farm owner first name"
                          sampleData="John"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.farmOwnerLastName"
                          label="Farm owner last name"
                          sampleData="Doe"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.numberOfFarmers"
                          label="Number of farmers"
                          sampleData="15"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.additionalCrops"
                          formatOptions={[{ key: 'type-select', label: 'Raw material' }]}
                          label="Additional crops"
                          sampleData="Cocoa, Coffee"
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.landOwnership"
                          label="Land ownership"
                          sampleData="Private ownership"
                          formatOptions={[{ key: 'type-select', label: 'Type select' }]}
                          infoTooltip={
                            <Box display="flex" flexDirection="column" gap={1}>
                              The Land ownership describes the type of ownership over the land. The
                              options are:
                              {Object.entries(LandOwnershipLabels).map(([key, value]) => (
                                <ThemeTypography key={key} variant="BODY_MEDIUM">
                                  <b>{value}:</b> {LandOwnershipDescriptions[key]}
                                </ThemeTypography>
                              ))}
                            </Box>
                          }
                        />
                        <Field
                          component={SettingColumItem}
                          name="columns.siteDescription"
                          label="Site description"
                          sampleData="A small cocoa farm located in the western region"
                        />
                      </FlexBox>
                    </Box>
                  </BackgroundContainer>
                </Box>
              </DialogContent>

              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <ThemeButton color="BLUE_ICE" onClick={handleClose}>
                  Cancel
                </ThemeButton>
                <ThemeButton
                  color="PRIMARY"
                  type="submit"
                  loading={isSubmitting}
                  disabled={!isValid}
                >
                  Save settings
                </ThemeButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </>
    </DialogDefault>
  );
};

export default DatasetTableSettingsDialogs;
